import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import {
  DataGridProProps,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridRowId,
  gridClasses,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { isIOS, isMobile } from 'react-device-detect';

import { Province } from '../../../@types/KingdomResponse';
import { IntelTabCols } from '../../../@types/Models';
import {
  ColumnOrder,
  ColumnVisibility,
  KdKanban,
  KdTabColumnVisibility,
  Tab,
} from '../../../@types/State';
import { useIntelState } from '../../../context/IntelState';
import useCols from '../../../hooks/useCols';
import { useDataRefresher } from '../../../hooks/useDataRefresher';
import DetailPanelContent from '../components/DetailPanelContent';
import GridFooter from '../components/GridFooter';
import GridViewToolBar from '../components/GridViewToolBar';
import { StripedDataGrid } from '../components/StripedDataGrid';
import { enrichProvinces } from '../helpers/helpers';

declare module '@mui/x-data-grid-pro' {
  interface FooterPropsOverrides {
    dataSet: Province[] | undefined;
    hideTotals: boolean;
    zoom: number;
  }
}

const cellCss = {
  paddingLeft: '5px',
  paddingRight: '5px',
};

const resizeOptions = {
  includeOutliers: true,
  includeHeaders: true,
};

interface Props {
  currentTab: Tab;
  dataSet: Province[] | undefined;
  plan: KdKanban | undefined;
  isViewingEnemy: boolean;
  isViewingRecent?: boolean;
  hideTotals?: boolean;
  zoom: number;
}

const MainGrid: React.FC<Props> = ({
  currentTab,
  dataSet,
  plan,
  isViewingEnemy,
  isViewingRecent,
  hideTotals,
  zoom,
}) => {
  const { intelState, intelDispatch } = useIntelState();
  const apiRef = useGridApiRef();
  const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>([]);
  useDataRefresher(isViewingEnemy);
  const allCols: IntelTabCols | undefined = useCols(dataSet);
  const colSet = isViewingEnemy ? 'enemy' : 'own';
  const cols = allCols && allCols[colSet][currentTab];

  const getCols = () => {
    if (intelState.columnVisibility[colSet][currentTab]) {
      const columnVisibility: ColumnVisibility =
        intelState.columnVisibility[colSet][currentTab];
      if (isViewingRecent) {
        columnVisibility.location = true;
      }
      return columnVisibility;
    }
  };

  const rows = useMemo(() => {
    return enrichProvinces(plan, dataSet);
  }, [dataSet, plan]);

  useEffect(() => {
    if (currentTab && apiRef.current && cols) {
      apiRef.current.autosizeColumns &&
        apiRef.current.autosizeColumns(resizeOptions);
    }
  }, [apiRef, cols, currentTab]);

  const handleColumnReorder = (a: GridColumnOrderChangeParams) => {
    const colReorder: ColumnOrder = {
      field: a.column.field,
      oldIndex: a.oldIndex,
      targetIndex: a.targetIndex,
    };
    intelDispatch({
      type: 'SET_TAB_COL_ORDER',
      payload: {
        colReorder,
        currentTab,
      },
    });
  };

  const hideDetailPanel = () => {
    setExpandedRowIds([] as GridRowId[]);
  };

  const getDetailPanelContent: DataGridProProps['getDetailPanelContent'] = ({
    row,
  }) => <DetailPanelContent row={row} hideDetailPanel={hideDetailPanel} />;

  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (newIds: GridRowId[]) => {
      setExpandedRowIds(
        newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds
      );
    },
    []
  );

  const handleColVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    intelDispatch({
      type: 'SET_TAB_COL_VISIBILITY',
      payload: {
        newModel,
        currentTab,
        colSet,
      },
    });
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    const fileReader = new FileReader();
    fileReader.readAsText(e.target.files[0], 'UTF-8');
    fileReader.onload = (e) => {
      if (e.target?.result && typeof e.target.result === 'string') {
        const json: KdTabColumnVisibility = JSON.parse(e.target.result);
        intelDispatch({
          type: 'SET_TAB_COL_VISIBILITY',
          payload: {
            json,
            currentTab,
            colSet,
          },
        });
        window.alert('Navigate to a different tab to see the changes.');
      }
    };
  };

  return rows && cols ? (
    <div
      style={{
        height: '100%',
        width: '100%',
      }}
    >
      <StripedDataGrid
        autoHeight
        rows={rows}
        apiRef={apiRef}
        initialState={{
          pinnedColumns: isMobile
            ? { left: ['slot'] }
            : { left: ['slot', 'name'] },
        }}
        autosizeOptions={resizeOptions}
        autosizeOnMount={true}
        columnVisibilityModel={getCols()}
        onColumnOrderChange={handleColumnReorder}
        onColumnVisibilityModelChange={handleColVisibilityChange}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={({ row }) => 'auto'}
        detailPanelExpandedRowIds={expandedRowIds}
        onDetailPanelExpandedRowIdsChange={
          handleDetailPanelExpandedRowIdsChange
        }
        columns={cols}
        density='compact'
        slots={{
          toolbar: GridViewToolBar,
          loadingOverlay: LinearProgress,
          columnUnsortedIcon: null,
          columnSortedAscendingIcon: null,
          columnSortedDescendingIcon: null,
          detailPanelExpandIcon: VisibilityIcon,
          detailPanelCollapseIcon: VisibilityOffIcon,
          footer: GridFooter,
        }}
        slotProps={{
          toolbar: {
            isViewingEnemy,
            handleFileUpload,
            isViewingRecent,
          },
          footer: { dataSet, hideTotals, zoom },
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        loading={intelState.loading}
        hideFooterPagination
        disableColumnMenu={isMobile}
        rowHeight={40}
        sx={{
          fontSize: `${isIOS ? zoom : 100}%`,
          '& .MuiDataGrid-columnHeader': cellCss,
          '& .MuiDataGrid-cell': cellCss,
          '& .spell-col': {
            paddingLeft: '2px',
            paddingRight: '2px',
          },
          '& .smallFont': {
            fontSize: 13,
          },
          '& .toggle-col': {
            paddingLeft: '2px',
            paddingRight: '2px',
            minWidth: '34px !important',
            maxWidth: '34px !important',
          },
          [`& .${gridClasses.columnSeparator}`]: {
            display: zoom < 100 ? 'none' : 'flex',
          },
        }}
        disableVirtualization={isMobile}
      />
    </div>
  ) : (
    <Typography variant='body1' sx={{ marginTop: 3 }}>
      No Kingdom Data found
    </Typography>
  );
};

export default MainGrid;
